import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import classNames from "classnames";
import { useUpdateInventory } from "../../../api/inventory/service/index";
import { useGetLocations } from "../../../api/locations/service";
import { useGetCities } from "../../../api/cities/service";
import { useGetStates } from "../../../api/states/service";
import { useGetCountries } from "../../../api/countries/service";
import { useGetChannels } from "../../../api/channels/service";
import { useNavigate } from "react-router-dom";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";
import { formatPriceWithDollar } from "../../../utils/currency";
import ReactDatetime from "components/CustomDatePicker";
import { useTranslation } from 'react-i18next';
import Select from "components/Select";
import { getSelectStyles } from "../../../utils/selectStyles";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import StatesInput from "../../../components/StatesInput";

const TitlingTab = forwardRef(
  ({ permission, inventoryId, inventoryData, activeColor = "blue", isLoading = false, isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);
  
  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".titling-tab.titling-tab--theme2",
            fieldsSelector: ".titling-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2
    ? { fontSize: "14px" }
    : { borderRadius: 0, fontSize: "14px" };
  const getDateInputProps = (extra = {}) => {
    const { className, ...rest } = extra;
    return {
      className: classNames("form-control", className),
      style: isTheme2 ? { fontSize: "14px" } : { borderRadius: 0, fontSize: "14px" },
      ...rest,
    };
  };
  
  const [formData, setFormData] = useState({
    title_number: '',
    title_status: '',
    title_comments: '',
    title_location: '',
    alt_title_location: '',
    // Lien Information
    lien_balance: 0,
    lien_name: '',
    lien_acct: '',
    lien_contact: '',
    lien_phone: '',
    lien_fax: '',
    lien_country: '',
    lien_address_1: '',
    lien_address_2: '',
    lien_zip_code: '',
    lien_city: '',
    lien_state: '',
    lien_county: '',
    // Previous Title Information
    title_received: '',
    previous_title_state: '',
    title_sent: '',
    title_transferred: '',
    // COT Information
    cot_name: '',
    cot_phone: '',
    cot_fax: '',
    cot_country: '',
    cot_address_1: '',
    cot_address_2: '',
    cot_zip_code: '',
    cot_city: '',
    cot_state: '',
    cot_county: '',
  });

  const updateMutation = useUpdateInventory();
  const navigate = useNavigate();
  const { data: titleLocationData } = useGetChannels("inventory_title_location");
  const { data: countriesData } = useGetCountries();
  const [showAddTitleLocation, setShowAddTitleLocation] = useState(false);




  useEffect(() => {
    if (inventoryData) {
      setFormData({
        title_number: inventoryData?.title_number || '',
        title_status: inventoryData?.title_status || '',
        title_comments: inventoryData?.title_comments || '',
        title_location: inventoryData?.title_location?.id || '',
        alt_title_location: inventoryData?.alt_title_location || '',
        // Lien Information
        lien_balance: inventoryData?.lien_balance !== undefined && inventoryData?.lien_balance !== null ? formatPriceWithDollar(inventoryData?.lien_balance) : '',
        lien_name: inventoryData?.lien_name || '',
        lien_acct: inventoryData?.lien_acct || '',
        lien_contact: inventoryData?.lien_contact || '',
        lien_phone: inventoryData?.lien_phone || '',
        lien_fax: inventoryData?.lien_fax || '',
        lien_country: inventoryData?.lien_country?.id ? String(inventoryData.lien_country.id) : '',
        lien_address_1: inventoryData?.lien_address_1 || '',
        lien_address_2: inventoryData?.lien_address_2 || '',
        lien_zip_code: inventoryData?.lien_zip_code || '',
        lien_city: inventoryData?.lien_city?.name || inventoryData?.lien_city || '',
        lien_state: inventoryData?.lien_state?.id ? String(inventoryData.lien_state.id) : '',
        lien_county: inventoryData?.lien_county || '',
        // Previous Title Information
        title_received: inventoryData?.title_received ? new Date(inventoryData?.title_received) : '',
        previous_title_state: inventoryData?.previous_title_state?.id ? {
          id: inventoryData.previous_title_state.id,
          abbreviation: inventoryData.previous_title_state.abbreviation
        } : null,
        title_sent: inventoryData?.title_sent ? new Date(inventoryData?.title_sent) : '',
        title_transferred: inventoryData?.title_transferred ? new Date(inventoryData?.title_transferred) : '',
        // COT Information
        cot_name: inventoryData?.cot_name || '',
        cot_phone: inventoryData?.cot_phone || '',
        cot_fax: inventoryData?.cot_fax || '',
        cot_country: inventoryData?.cot_country?.id ? String(inventoryData.cot_country.id) : '',
        cot_address_1: inventoryData?.cot_address_1 || '',
        cot_address_2: inventoryData?.cot_address_2 || '',
        cot_zip_code: inventoryData?.cot_zip_code || '',
        cot_city: inventoryData?.cot_city?.name || inventoryData?.cot_city || '',
        cot_state: inventoryData?.cot_state?.id ? String(inventoryData.cot_state.id) : '',
        cot_county: inventoryData?.cot_county || '',
      });
    }
  }, [inventoryData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Clear state when country changes
      if (field === 'cot_country') {
        newData.cot_state = '';
      }
      if (field === 'lien_country') {
        newData.lien_state = '';
      }
      
      return newData;
    });
  };

  const handleDateChange = (field, date) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleLocationChange = (value) => {
    if (value === "add_new_lot") {
      setShowAddTitleLocation(true);
    } else {
      handleInputChange('title_location', value);
    }
  };

  // Parent component'e fonksiyonları expose et
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      const updateData = {
        titling: {
          title_number: formData.title_number || null,
          title_status: formData.title_status || null,
          title_comments: formData.title_comments || null,
          title_location: formData.title_location || null,
          alt_title_location: formData.alt_title_location || null,
          // Lien Information
          lien_balance: formData.lien_balance ? parseFloat(formData.lien_balance.toString().replace(/[^\d.]/g, '')) : null,
          lien_name: formData.lien_name || null,
          lien_acct: formData.lien_acct || null,
          lien_contact: formData.lien_contact || null,
          lien_phone: formData.lien_phone || null,
          lien_fax: formData.lien_fax || null,
          lien_country: formData.lien_country || null,
          lien_address_1: formData.lien_address_1 || null,
          lien_address_2: formData.lien_address_2 || null,
          lien_zip_code: formData.lien_zip_code || null,
          lien_city: formData.lien_city || null,
          lien_state: formData.lien_state || null,
          lien_county: formData.lien_county || null,
          // Previous Title Information
          title_received: formData.title_received || null,
          previous_title_state: formData.previous_title_state || null,
          title_sent: formData.title_sent || null,
          title_transferred: formData.title_transferred || null,
          // COT Information
          cot_name: formData.cot_name || null,
          cot_phone: formData.cot_phone || null,
          cot_fax: formData.cot_fax || null,
          cot_country: formData.cot_country || null,
          cot_address_1: formData.cot_address_1 || null,
          cot_address_2: formData.cot_address_2 || null,
          cot_zip_code: formData.cot_zip_code || null,
          cot_city: formData.cot_city || null,
          cot_state: formData.cot_state || null,
          cot_county: formData.cot_county || null,
        }
      };
      updateMutation.mutate({ id: inventoryId, data: updateData });
      return { success: true };
    }
  }));

  // Title Status options - API enum değerlerine göre güncellendi
  const titleStatusOptions = [
    { value: '', label: t('titlingTab.selectStatus') },
    { value: 'Unknown Status', label: t('titlingTab.unknownStatus') },
    { value: 'Clear Title', label: t('titlingTab.clearTitle') },
    { value: 'Flood Title', label: t('titlingTab.floodTitle') },
    { value: 'Recondition Title', label: t('titlingTab.reconditionTitle') },
    { value: 'Salvage Title', label: t('titlingTab.salvageTitle') },
    { value: 'Held Title', label: t('titlingTab.heldTitle') },
    { value: 'No Title', label: t('titlingTab.noTitle') }
  ];

  // Location options
  const locationOptions = [
    { value: '', label: t('titlingTab.selectLocation') },
    ...(titleLocationData?.results || []).map(location => ({
      value: String(location.id),
      label: location.name
    })),
    { value: 'add_new_lot', label: '+ Add New Lot' }
  ];

  // Country options
  const countryOptions = [
    { value: '', label: t('titlingTab.selectCountry') },
    ...(countriesData?.data || []).map(country => ({
      value: String(country.id),
      label: country.name
    }))
  ];

  // Country-based state options
  const selectedCotCountry = useMemo(() => {
    return countriesData?.data?.find(
      (country) => String(country.id) === String(formData.cot_country)
    );
  }, [countriesData, formData.cot_country]);

  const selectedLienCountry = useMemo(() => {
    return countriesData?.data?.find(
      (country) => String(country.id) === String(formData.lien_country)
    );
  }, [countriesData, formData.lien_country]);

  const cotStateOptions = selectedCotCountry?.states || [];
  const lienStateOptions = selectedLienCountry?.states || [];


  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className='sr-only'>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("titling-tab", { "titling-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {showAddTitleLocation && <ChannelsEmbedded
        activeColor={activeColor}
        defaultType="inventory_title_location"
        onClose={() => setShowAddTitleLocation(false)}
        onCreated={(created) => {
          if (created?.id) {
            handleInputChange("title_location", String(created.id));
          } else if (created?.name) {
            handleInputChange("title_location", String(created.name));
          }
        }}
      />}
      {!showAddTitleLocation && 
    <div className={classNames("row", { "titling-tab-fields": isTheme2 })}>
      {/* Title Information Card */}
      <div className="col-md-6 mb-4">
        <div className="card">
          <div className="card-header font-weight-bold text-uppercase">
            {t('titlingTab.titleInformation')}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="form-group col-md-6">
                <label>{t('titlingTab.titleStatus')}</label>
                <Select
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('titlingTab.selectStatus')}
                  name="title_status"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.title_status ? {
                    value: formData.title_status,
                    label: titleStatusOptions.find(option => option.value === formData.title_status)?.label || formData.title_status
                  } : null}
                  onChange={(option) => handleInputChange('title_status', option?.value || '')}
                  options={titleStatusOptions}
                  isClearable
                />
              </div>
              
              <div className="form-group col-md-6">
                <label>{t('titlingTab.titleComments')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.title_comments}
                  onChange={(e) => handleInputChange('title_comments', e.target.value)}
                  placeholder={t('titlingTab.enterComments')}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>{t('titlingTab.titleLocation')}</label>
                  <Select
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t('titlingTab.selectLocation')}
                    name="title_location"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={formData.title_location ? {
                      value: formData.title_location,
                      label: formData.title_location === "add_new_lot" 
                        ? '+ Add New Lot'
                        : titleLocationData?.results?.find(loc => String(loc.id) === String(formData.title_location))?.name || formData.title_location
                    } : null}
                    onChange={(option) => {
                      if (option?.value === "add_new_lot") {
                        handleLocationChange("add_new_lot");
                      } else {
                        handleLocationChange(option?.value || "");
                      }
                    }}
                    options={locationOptions}
                    isClearable
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>{t('titlingTab.altTitleLocation')}</label>
                  <input
                    style={inputStyle}
                    type="text"
                    className="form-control"
                    value={formData.alt_title_location}
                    onChange={(e) => handleInputChange('alt_title_location', e.target.value)}
                    placeholder={t('titlingTab.enterAlternativeTitleLocation')}
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>{t('titlingTab.titleNumber')}</label>
              <input
                    style={inputStyle}
                type="text"
                className="form-control"
                value={formData.title_number}
                onChange={(e) => handleInputChange('title_number', e.target.value)}
                placeholder={t('titlingTab.enterTitleNumber')}
              />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header font-weight-bold text-uppercase">
            {t('titlingTab.previousTitleInformation')}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="form-group col-md-6">
                <label>Title Received</label>
                <ReactDatetime
                  inputProps={getDateInputProps({ placeholder: "Select date..." })}
                  value={formData.title_received}
                  onChange={(date) => handleDateChange('title_received', date)}
                  dateFormat="MM-DD-YYYY"
                  timeFormat={false}
                />
              </div>
              
              <div className="form-group col-md-6">
                <label>Previous Title State</label>
                <StatesInput
                  name="previous_title_state"
                  value={formData.previous_title_state}
                  onChange={(stateObject) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      previous_title_state: stateObject ? {
                        id: stateObject.id,
                        abbreviation: stateObject.abbreviation
                      } : null
                    }));
                  }}
                  customSelectStyles={selectStyles}
                  placeholder={t('titlingTab.selectState')}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <label>Title Sent</label>
                <ReactDatetime
                  inputProps={getDateInputProps({ placeholder: "Select date..." })}
                  value={formData.title_sent}
                  onChange={(date) => handleDateChange('title_sent', date)}
                  dateFormat="MM-DD-YYYY"
                  timeFormat={false}
                />
              </div>
              
              <div className="form-group col-md-6">
                <label>Title Transferred</label>
                <ReactDatetime
                  inputProps={getDateInputProps({ placeholder: "Select date..." })}
                  value={formData.title_transferred}
                  onChange={(date) => handleDateChange('title_transferred', date)}
                  dateFormat="MM-DD-YYYY"
                  timeFormat={false}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header font-weight-bold text-uppercase">
            {t('titlingTab.cotInformation')}
          </div>
          <div className="card-body">
            {/* COT Name and Phone - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>COT Name</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_name}
                  onChange={(e) => handleInputChange('cot_name', e.target.value)}
                  placeholder={t('titlingTab.enterCOTName')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Phone</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_phone}
                  onChange={(e) => handleInputChange('cot_phone', e.target.value)}
                  placeholder={t('titlingTab.enterPhoneNumber')}
                />
              </div>
            </div>

            {/* Fax and Country - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>Fax</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_fax}
                  onChange={(e) => handleInputChange('cot_fax', e.target.value)}
                  placeholder={t('titlingTab.enterFaxNumber')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Country</label>
                <Select
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('titlingTab.selectCountry')}
                  name="cot_country"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.cot_country ? {
                    value: formData.cot_country,
                    label: countriesData?.data?.find(country => String(country.id) === String(formData.cot_country))?.name || formData.cot_country
                  } : null}
                  onChange={(option) => handleInputChange('cot_country', option?.value || '')}
                  options={countryOptions}
                  isClearable
                />
              </div>
            </div>

            {/* Address 1 and Address 2 - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>Address 1</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_address_1}
                  onChange={(e) => handleInputChange('cot_address_1', e.target.value)}
                  placeholder={t('titlingTab.enterAddressLine1')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Address 2</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_address_2}
                  onChange={(e) => handleInputChange('cot_address_2', e.target.value)}
                  placeholder={t('titlingTab.enterAddressLine2')}
                />
              </div>
            </div>

            {/* ZIP Code and City - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>ZIP Code</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_zip_code}
                  onChange={(e) => handleInputChange('cot_zip_code', e.target.value)}
                  placeholder={t('titlingTab.enterZIPCode')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>City</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_city}
                  onChange={(e) => handleInputChange('cot_city', e.target.value)}
                  placeholder={t('titlingTab.enterCity')}
                />
              </div>
            </div>

            {/* State and County - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>State</label>
                <Select
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('titlingTab.selectState')}
                  name="cot_state"
                  isSearchable
                  closeMenuOnSelect={true}
                  value={formData.cot_state ? {
                    value: formData.cot_state,
                    label: cotStateOptions.find(state => String(state.id) === String(formData.cot_state))?.name || formData.cot_state
                  } : null}
                  onChange={(option) => handleInputChange('cot_state', option?.value || '')}
                  options={[
                    { value: '', label: t('titlingTab.selectState') },
                    ...cotStateOptions.map(state => ({
                      value: String(state.id),
                      label: state.name
                    }))
                  ]}
                  isClearable
                />
              </div>
              <div className="form-group col-md-6">
                <label>County</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.cot_county}
                  onChange={(e) => handleInputChange('cot_county', e.target.value)}
                  placeholder={t('titlingTab.enterCounty')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lien Information Card */}
      <div className="col-md-6 mb-4">
        <div className="card">
          <div className="card-header font-weight-bold text-uppercase">
            Lien Information
          </div>
          <div className="card-body">
            {/* Lien Balance and Lien Name - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>{t('titlingTab.lienBalance')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_balance}
                  onChange={(e) => handleInputChange('lien_balance', e.target.value)}
                  onBlur={(e) => {
                    if (formData.lien_balance) {
                      const numericValue = parseFloat(formData.lien_balance.toString().replace(/[^\d.]/g, ''));
                      if (!isNaN(numericValue)) {
                        setFormData(prev => ({
                          ...prev,
                          lien_balance: formatPriceWithDollar(numericValue)
                        }));
                      }
                    }
                  }}
                  onFocus={(e) => {
                    if (formData.lien_balance) {
                      const raw = formData.lien_balance.toString().replace(/[^\d.]/g, '');
                      setFormData(prev => ({ ...prev, lien_balance: raw }));
                    }
                  }}
                  placeholder={t('titlingTab.enterAmount')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>{t('titlingTab.lienName')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_name}
                  onChange={(e) => handleInputChange('lien_name', e.target.value)}
                  placeholder={t('titlingTab.enterLienName')}
                />
              </div>
            </div>

            {/* Lien Acct and Contact - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>{t('titlingTab.lienAcct')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_acct}
                  onChange={(e) => handleInputChange('lien_acct', e.target.value)}
                  placeholder={t('titlingTab.enterLienAccount')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Contact</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_contact}
                  onChange={(e) => handleInputChange('lien_contact', e.target.value)}
                  placeholder={t('titlingTab.enterContactName')}
                />
              </div>
            </div>

            {/* Phone and Fax - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>Phone</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_phone}
                  onChange={(e) => handleInputChange('lien_phone', e.target.value)}
                  placeholder={t('titlingTab.enterPhoneNumber')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Fax</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_fax}
                  onChange={(e) => handleInputChange('lien_fax', e.target.value)}
                  placeholder={t('titlingTab.enterFaxNumber')}
                />
              </div>
            </div>

            {/* Country and Address 1 - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>Country</label>
                <Select
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('titlingTab.selectCountry')}
                  name="lien_country"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.lien_country ? {
                    value: formData.lien_country,
                    label: countriesData?.data?.find(country => String(country.id) === String(formData.lien_country))?.name || formData.lien_country
                  } : null}
                  onChange={(option) => handleInputChange('lien_country', option?.value || '')}
                  options={countryOptions}
                  isClearable
                />
              </div>
              <div className="form-group col-md-6">
                <label>Address 1</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_address_1}
                  onChange={(e) => handleInputChange('lien_address_1', e.target.value)}
                  placeholder={t('titlingTab.enterAddressLine1')}
                />
              </div>
            </div>

            {/* Address 2 and ZIP Code - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>Address 2</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_address_2}
                  onChange={(e) => handleInputChange('lien_address_2', e.target.value)}
                  placeholder={t('titlingTab.enterAddressLine2')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>ZIP Code</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_zip_code}
                  onChange={(e) => handleInputChange('lien_zip_code', e.target.value)}
                  placeholder={t('titlingTab.enterZIPCode')}
                />
              </div>
            </div>

            {/* City and State - yan yana */}
            <div className="row">
              <div className="form-group col-md-6">
                <label>City</label>
                <input
                  style={inputStyle}
                  type="text"
                  className="form-control"
                  value={formData.lien_city}
                  onChange={(e) => handleInputChange('lien_city', e.target.value)}
                  placeholder={t('titlingTab.enterCity')}
                />
              </div>
              <div className="form-group col-md-6">
                <label>State</label>
                <Select
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('titlingTab.selectState')}
                  name="lien_state"
                  isSearchable
                  closeMenuOnSelect={true}
                  value={formData.lien_state ? {
                    value: formData.lien_state,
                    label: lienStateOptions.find(state => String(state.id) === String(formData.lien_state))?.name || formData.lien_state
                  } : null}
                  onChange={(option) => handleInputChange('lien_state', option?.value || '')}
                  options={[
                    { value: '', label: t('titlingTab.selectState') },
                    ...lienStateOptions.map(state => ({
                      value: String(state.id),
                      label: state.name
                    }))
                  ]}
                  isClearable
                />
              </div>
            </div>

            {/* County - tek satır */}
            <div className="form-group">
              <label>County</label>
              <input
                    style={inputStyle}
                type="text"
                className="form-control"
                value={formData.lien_county}
                onChange={(e) => handleInputChange('lien_county', e.target.value)}
                placeholder="Enter county..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    }
    </div>
  );
}
);

export default TitlingTab; 